// src/components/LoadingState.js

import React from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    useTheme,
    Avatar,
    Zoom,
    Fade,
    CircularProgress
} from '@mui/material';
import {
    PointOfSale,
    ShoppingCart,
    Storefront,
    ReceiptLong,
    Inventory,
    Payments,
    BusinessCenter,
    LocalMall,
    Calculate,
    TrendingUp,
    AccountBalance,
    QrCodeScanner
} from '@mui/icons-material';

const LoadingState = ({
    message = "Loading...",
    icon = null,
    fullPage = false,
    size = "medium",
    variant = "default"
}) => {
    const theme = useTheme();

    // Size mappings
    const sizeMap = {
        small: {
            iconSize: 36,
            avatarSize: 56,
            fontSize: 'subtitle1',
            progressHeight: 3,
            containerHeight: fullPage ? '100vh' : '200px',
            progressWidth: '200px'
        },
        medium: {
            iconSize: 48,
            avatarSize: 80,
            fontSize: 'h6',
            progressHeight: 4,
            containerHeight: fullPage ? '100vh' : '300px',
            progressWidth: '300px'
        },
        large: {
            iconSize: 64,
            avatarSize: 110,
            fontSize: 'h5',
            progressHeight: 6,
            containerHeight: fullPage ? '100vh' : '400px',
            progressWidth: '400px'
        }
    };

    const sizes = sizeMap[size];

    // POS-specific icons for different variants
    const getIcon = () => {
        if (icon) return icon;

        switch (variant) {
            case 'pos':
                return <PointOfSale sx={{ fontSize: sizes.iconSize }} />;
            case 'cart':
                return <ShoppingCart sx={{ fontSize: sizes.iconSize }} />;
            case 'store':
                return <Storefront sx={{ fontSize: sizes.iconSize }} />;
            case 'receipt':
                return <ReceiptLong sx={{ fontSize: sizes.iconSize }} />;
            case 'inventory':
                return <Inventory sx={{ fontSize: sizes.iconSize }} />;
            case 'payment':
                return <Payments sx={{ fontSize: sizes.iconSize }} />;
            case 'business':
                return <BusinessCenter sx={{ fontSize: sizes.iconSize }} />;
            case 'retail':
                return <LocalMall sx={{ fontSize: sizes.iconSize }} />;
            case 'accounting':
                return <Calculate sx={{ fontSize: sizes.iconSize }} />;
            case 'analytics':
                return <TrendingUp sx={{ fontSize: sizes.iconSize }} />;
            case 'banking':
                return <AccountBalance sx={{ fontSize: sizes.iconSize }} />;
            case 'scanner':
                return <QrCodeScanner sx={{ fontSize: sizes.iconSize }} />;
            default:
                return <PointOfSale sx={{ fontSize: sizes.iconSize }} />;
        }
    };

    // Get gradient based on variant
    const getGradient = () => {
        switch (variant) {
            case 'pos':
            case 'scanner':
                return `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`;
            case 'cart':
            case 'retail':
                return `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`;
            case 'inventory':
            case 'store':
                return `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`;
            case 'payment':
            case 'banking':
                return `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`;
            case 'analytics':
            case 'accounting':
                return `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`;
            default:
                return `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`;
        }
    };

    // Get glow color
    const getGlowColor = () => {
        switch (variant) {
            case 'pos':
            case 'scanner':
                return theme.palette.primary.main;
            case 'cart':
            case 'retail':
                return theme.palette.secondary.main;
            case 'inventory':
            case 'store':
                return theme.palette.success.main;
            case 'payment':
            case 'banking':
                return theme.palette.info.main;
            case 'analytics':
            case 'accounting':
                return theme.palette.warning.main;
            default:
                return theme.palette.primary.main;
        }
    };

    const glowColor = getGlowColor();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: sizes.containerHeight,
                width: '100%',
                gap: 2,
                p: 4,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Background decorative elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '20%',
                    left: '10%',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${glowColor}15 0%, transparent 70%)`,
                    filter: 'blur(40px)',
                    animation: 'float-slow 8s ease-in-out infinite',
                    '@keyframes float-slow': {
                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                        '50%': { transform: 'translate(20px, -20px) scale(1.1)' },
                    }
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '20%',
                    right: '10%',
                    width: '150px',
                    height: '150px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${glowColor}10 0%, transparent 70%)`,
                    filter: 'blur(40px)',
                    animation: 'float-slow 6s ease-in-out infinite reverse',
                }}
            />

            <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    {/* Outer rotating ring */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -15,
                            left: -15,
                            width: sizes.avatarSize + 30,
                            height: sizes.avatarSize + 30,
                            borderRadius: '50%',
                            border: `3px solid transparent`,
                            borderTopColor: glowColor,
                            borderRightColor: glowColor,
                            animation: 'spin 2s linear infinite',
                            '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' },
                            },
                            opacity: 0.6,
                            zIndex: 0
                        }}
                    />
                    
                    {/* Inner rotating ring (opposite direction) */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -8,
                            left: -8,
                            width: sizes.avatarSize + 16,
                            height: sizes.avatarSize + 16,
                            borderRadius: '50%',
                            border: `2px solid transparent`,
                            borderBottomColor: glowColor,
                            borderLeftColor: glowColor,
                            animation: 'spin 3s linear infinite reverse',
                            opacity: 0.4,
                            zIndex: 0
                        }}
                    />

                    {/* Main avatar */}
                    <Box
                        sx={{
                            animation: 'pulse-glow 2s ease-in-out infinite',
                            '@keyframes pulse-glow': {
                                '0%': { 
                                    transform: 'scale(1)',
                                    boxShadow: `0 0 20px ${glowColor}30`
                                },
                                '50%': { 
                                    transform: 'scale(1.05)',
                                    boxShadow: `0 0 40px ${glowColor}50`
                                },
                                '100%': { 
                                    transform: 'scale(1)',
                                    boxShadow: `0 0 20px ${glowColor}30`
                                },
                            },
                            zIndex: 1
                        }}
                    >
                        <Avatar
                            sx={{
                                width: sizes.avatarSize,
                                height: sizes.avatarSize,
                                background: getGradient(),
                                color: 'white',
                                boxShadow: `0 8px 32px ${glowColor}40`,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {getIcon()}
                        </Avatar>
                    </Box>
                </Box>
            </Zoom>

            <Fade in={true} timeout={800}>
                <Box sx={{ 
                    textAlign: 'center', 
                    maxWidth: 450, 
                    width: '100%',
                    mt: 2
                }}>
                    <Typography
                        variant={sizes.fontSize}
                        sx={{
                            color: theme.palette.text.primary,
                            mb: 1,
                            fontWeight: 600,
                            letterSpacing: '0.3px'
                        }}
                    >
                        {message}
                    </Typography>
                    
                    <Typography
                        variant="caption"
                        sx={{
                            color: theme.palette.text.secondary,
                            display: 'block',
                            mb: 3
                        }}
                    >
                        Please wait while we process your request
                    </Typography>

                    {/* Custom progress bar */}
                    <Box sx={{ 
                        width: sizes.progressWidth, 
                        maxWidth: '100%', 
                        mx: 'auto',
                        position: 'relative'
                    }}>
                        <LinearProgress
                            sx={{
                                height: sizes.progressHeight,
                                borderRadius: sizes.progressHeight / 2,
                                backgroundColor: `${glowColor}20`,
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: sizes.progressHeight / 2,
                                    background: `linear-gradient(90deg, ${glowColor} 0%, ${glowColor}99 50%, ${glowColor} 100%)`,
                                    backgroundSize: '200% 100%',
                                    animation: 'shimmer 2s ease-in-out infinite',
                                    '@keyframes shimmer': {
                                        '0%': { backgroundPosition: '200% 0' },
                                        '100%': { backgroundPosition: '-200% 0' },
                                    }
                                }
                            }}
                        />
                    </Box>

                    {/* Loading dots */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: 0.8, 
                        mt: 2 
                    }}>
                        {[...Array(3)].map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: size === 'small' ? 6 : size === 'medium' ? 8 : 10,
                                    height: size === 'small' ? 6 : size === 'medium' ? 8 : 10,
                                    borderRadius: '50%',
                                    background: getGradient(),
                                    animation: `bounce-dot 1.2s ease-in-out ${i * 0.15}s infinite`,
                                    '@keyframes bounce-dot': {
                                        '0%, 100%': { 
                                            transform: 'translateY(0)',
                                            opacity: 0.4
                                        },
                                        '50%': { 
                                            transform: 'translateY(-8px)',
                                            opacity: 1
                                        },
                                    },
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Fade>
        </Box>
    );
};

export default LoadingState;